<template>
  <div class="modal" :class="{ 'is-active': isVisible }">
    <div class="modal-background" @click="close"></div>
    <div class="modal-content modal-medium">
      <div class="modal-inner-wrapper">
        <a class="modalclose" @click="close">
          <i class="fas fa-times"></i>
        </a>

        <!-- Header -->
        <h1>
          ARE YOU SURE YOU WANT TO CANCEL?
        </h1>

        <!-- Event Info -->
        <div class="event-section" v-if="event">
          <EventPreview
            :event="event"
            :selectedDate="selectedDate"
            :venue="venue"
          />
          <div class="warning-icon" v-if="willRefund">
            <img src="/img/warning.png" alt="warning" />
          </div>
        </div>

        <!-- Customer Info -->
        <div class="customer-info" v-if="order">
          <div class="customer-name">
            {{ order.firstName }} {{ order.lastName }}
          </div>
          <div class="customer-email">{{ order.email }}</div>
          <div class="customer-tickets">
            {{ totalTickets }} {{ order.tierName }}
            <span v-if="order.checkedin > 0" class="checked-in-note">
              ({{ order.checkedin }} already checked in)
            </span>
          </div>
        </div>

        <!-- Ticket Quantity Selector -->
        <div class="ticket-selector">
          <p class="selector-label">How many tickets do you want to cancel?</p>
          <div class="quantity-buttons">
            <button
              v-for="num in totalTickets"
              :key="num"
              class="quantity-btn"
              :class="{ active: selectedQuantity === num }"
              @click="selectedQuantity = num"
            >
              {{ num }}
            </button>
          </div>
        </div>

        <!-- Refund Checkbox -->
        <div class="refund-section" v-if="hasPaymentToRefund">
          <label
            class="checkbox-container"
            :class="{ disabled: selectedQuantity !== totalTickets }"
          >
            <input
              type="checkbox"
              v-model="willRefund"
              :disabled="selectedQuantity !== totalTickets"
            />
            <span class="checkmark"></span>
            Refund purchase
          </label>
          <p v-if="willRefund" class="warning">
            Refunded purchases can not be reversed
          </p>
          <p v-if="selectedQuantity !== totalTickets" class="refund-note">
            Refund is only available when cancelling all tickets
          </p>
        </div>

        <!-- Action Buttons -->
        <div class="columns">
          <div class="column">
            <button
              class="button is-fullwidth"
              @click="close"
              :disabled="isProcessing"
            >
              NO, OOPS DON'T
            </button>
          </div>
          <div class="column">
            <button
              class="button is-fullwidth is-primary"
              @click="confirmCancel"
              :disabled="isProcessing || selectedQuantity === 0"
            >
              <span v-if="isProcessing" class="icon">
                <i class="fas fa-spinner fa-spin"></i>
              </span>
              {{ isProcessing ? "PROCESSING..." : "YES, CANCEL TICKET" }}
            </button>
          </div>
        </div>

        <!-- Processing Status -->
        <div v-if="processingMessage" class="processing-status">
          {{ processingMessage }}
        </div>

        <!-- Error Message -->
        <div v-if="errorMessage" class="error-message">
          {{ errorMessage }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import firebase from "firebase/app";
import "firebase/firestore";
import EventPreview from "@/components/event-preview";

export default {
  name: "CancelOrderModal",
  components: {
    EventPreview
  },
  props: {
    isVisible: {
      type: Boolean,
      default: false
    },
    order: {
      type: Object,
      default: null
    },
    event: {
      type: Object,
      default: null
    },
    venue: {
      type: Object,
      default: null
    },
    eventId: {
      type: String,
      default: null
    },
    dateId: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      selectedQuantity: 0, // Will be set to totalTickets when modal opens
      willRefund: false,
      isProcessing: false,
      processingMessage: "",
      errorMessage: "",
      requestListener: null
    };
  },
  computed: {
    ...mapGetters({
      user: "user"
    }),
    totalTickets() {
      // Remaining tickets available to cancel (quantity - already checked in)
      if (!this.order) return 0;
      const quantity = parseInt(this.order.quantity) || 0;
      const checkedin = parseInt(this.order.checkedin) || 0;
      return quantity - checkedin;
    },
    selectedDate() {
      // Return the date object if available, otherwise null
      return this.dateId ? { id: this.dateId } : null;
    },
    hasPaymentToRefund() {
      // Only show refund option if there's a non-zero total to refund
      // AND no tickets have been checked in (can't refund partial use)
      if (!this.order) return false;
      const hasPayment = this.order.total && parseFloat(this.order.total) > 0;
      const checkedin = parseInt(this.order.checkedin) || 0;
      return hasPayment && checkedin === 0;
    }
  },
  watch: {
    selectedQuantity(newVal) {
      // Disable refund checkbox if not cancelling all tickets
      if (newVal !== this.totalTickets) {
        this.willRefund = false;
      }
    },
    isVisible(newVal) {
      if (newVal) {
        // Reset form when modal opens, default to cancelling all tickets
        this.selectedQuantity = this.totalTickets;
        this.willRefund = false;
        this.isProcessing = false;
        this.processingMessage = "";
        this.errorMessage = "";
      }
    }
  },
  beforeDestroy() {
    // Clean up listener on component destroy
    if (this.requestListener) {
      this.requestListener();
      this.requestListener = null;
    }
  },
  methods: {
    close() {
      if (!this.isProcessing) {
        // Clean up listener when closing modal
        if (this.requestListener) {
          this.requestListener();
          this.requestListener = null;
        }
        this.$emit("close");
      }
    },
    async confirmCancel() {
      if (this.willRefund) {
        await this.cancelWithRefund();
      } else {
        await this.cancelWithoutRefund();
      }
    },
    async cancelWithRefund() {
      this.isProcessing = true;
      this.processingMessage = "Initiating refund...";
      this.errorMessage = "";

      // Clean up any existing listener
      if (this.requestListener) {
        this.requestListener();
        this.requestListener = null;
      }

      try {
        const reqRef = await this.$store.dispatch("cancelOrderWithRefund", {
          orderId: this.order.orderId
        });

        this.processingMessage = "Processing refund...";

        // Set up listener to watch for status changes
        this.requestListener = reqRef.onSnapshot(snapshot => {
          const data = snapshot.data();
          if (data) {
            if (data.status === "complete") {
              this.processingMessage = "Refund completed successfully!";

              // Update willcall record status to refunded
              this.updateWillcallStatus("refunded");

              // Clean up listener
              if (this.requestListener) {
                this.requestListener();
                this.requestListener = null;
              }

              // Reset processing state and close modal after brief delay
              setTimeout(() => {
                this.isProcessing = false;
                this.$emit("cancelled", {
                  refunded: true,
                  quantity: this.selectedQuantity
                });
                this.close();
              }, 2000);
            } else if (data.status === "Error") {
              this.isProcessing = false;
              this.processingMessage = "";
              this.errorMessage =
                data.errorMessage || "An error occurred processing the refund";

              // Clean up listener
              if (this.requestListener) {
                this.requestListener();
                this.requestListener = null;
              }
            } else if (data.status === "starting") {
              this.processingMessage = "Validating refund request...";
            }
            // Keep processing for "submitted" status
          }
        });
      } catch (error) {
        console.error("Error initiating refund:", error);
        this.isProcessing = false;
        this.processingMessage = "";
        this.errorMessage = "Failed to initiate refund request";
      }
    },
    async cancelWithoutRefund() {
      this.isProcessing = true;
      this.processingMessage = "Cancelling tickets...";
      this.errorMessage = "";

      try {
        // Update willcall record status
        await this.updateWillcallStatus("cancelled");

        // Reset processing state and close modal immediately
        this.isProcessing = false;
        this.processingMessage = "";
        this.$emit("cancelled", {
          refunded: false,
          quantity: this.selectedQuantity
        });
        this.close();
      } catch (error) {
        console.error("Error cancelling tickets:", error);
        this.isProcessing = false;
        this.processingMessage = "";
        this.errorMessage = "Failed to cancel tickets";
      }
    },
    async updateWillcallStatus(status) {
      try {
        const db = firebase.firestore();

        // Get current values (normalized in computed properties)
        const currentQuantity = parseInt(this.order.quantity) || 0;
        const checkedin = parseInt(this.order.checkedin) || 0;
        const existingCancelled = parseInt(this.order.cancelled) || 0;

        // Calculate new values
        // quantity decreases by the amount cancelled
        const newQuantity = currentQuantity - this.selectedQuantity;
        const newCancelledTotal = existingCancelled + this.selectedQuantity;

        // Determine final status
        // - If no remaining tickets AND no check-ins: cancelled/refunded
        // - Otherwise: mixed (has combination of states)
        let finalStatus;
        if (newQuantity === 0 && checkedin === 0) {
          finalStatus = status; // 'cancelled' or 'refunded'
        } else {
          finalStatus = "mixed";
        }

        const updateData = {
          status: finalStatus,
          quantity: newQuantity,
          cancelled: newCancelledTotal,
          checkedin: checkedin // Ensure checkedin field exists for old records
        };

        await db
          .collection("events")
          .doc(this.eventId)
          .collection("userData")
          .doc("audienceReport")
          .collection("dates")
          .doc(this.dateId)
          .collection("audience")
          .doc(this.order.id)
          .update(updateData);

        // Dispatch to store to update local state
        this.$store.dispatch("cancelWillcalll", this.order);
      } catch (error) {
        console.error("Error updating willcall status:", error);
        throw error;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import "@/assets/variables.scss";

.warning {
  color: $oops-red;
}

.modal-content {
  background-color: white;
  border-radius: $radius;
  position: relative;
}

.modal-inner-wrapper {
  margin: 30px;
  margin-top: 0;
  margin-right: 0;
  max-height: 70vh;
  overflow-y: auto;
  padding: 30px;
  padding-top: 30px;
  padding-right: 30px;
  position: relative;
}

.modalclose {
  background: $gray1;
  font-size: 1.2em;
  height: 40px;
  width: 40px;
  color: white;
  text-align: center;
  line-height: 42px;
  border-top-right-radius: $radius;
  position: absolute;
  top: 0;
  right: 0;
  text-decoration: none;
  cursor: pointer;
}

.modalclose:hover {
  background: $gray2;
}

.event-section {
  position: relative;
  margin-bottom: 1.5rem;
}

.warning-icon {
  position: absolute;
  top: 0;
  right: 0;
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 40px;
    height: 40px;
  }
}

.customer-info {
  margin-bottom: 1.5rem;
}

.customer-name {
  font-weight: 600;
  color: $gray1;
}

.customer-email {
  color: $gray3;
  font-size: 0.875rem;
}

.customer-tickets {
  color: $gray2;
  font-weight: 500;
  margin-top: 0.25rem;
}

.checked-in-note {
  color: $gray4;
  font-weight: 400;
  font-size: 0.875rem;
}

.ticket-selector {
  margin-bottom: 1.5rem;
}

.selector-label {
  font-weight: 600;
  color: $gray2;
  margin-bottom: 1rem;
}

.quantity-buttons {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.quantity-btn {
  width: 60px;
  height: 50px;
  border: 2px solid $gray4;
  background: white;
  border-radius: 8px;
  font-weight: 600;
  color: $gray2;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: $active-green;
  }

  &.active {
    border-color: $active-green;
    background-color: $active-green;
    color: white;
  }
}

.refund-section {
  margin-bottom: 1.5rem;
}

.refund-note {
  font-size: 0.75rem;
  color: $gray4;
  margin-top: 0.5rem;
  font-style: italic;
}

.checkbox-container {
  display: flex;
  align-items: center;
  cursor: pointer;
  font-weight: 500;
  color: $gray2;

  &.disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  input[type="checkbox"] {
    display: none;
  }

  input[type="checkbox"]:disabled + .checkmark {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .checkmark {
    width: 20px;
    height: 20px;
    border: 2px solid $gray4;
    border-radius: 4px;
    margin-right: 0.75rem;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;

    &::after {
      content: "✓";
      color: white;
      font-weight: bold;
      opacity: 0;
      transition: opacity 0.2s ease;
    }
  }

  input[type="checkbox"]:checked + .checkmark {
    background-color: $active-green;
    border-color: $active-green;

    &::after {
      opacity: 1;
    }
  }
}

.processing-status {
  text-align: center;
  color: $active-green;
  font-weight: 500;
  font-size: 0.875rem;
  margin-top: 1rem;
}

.error-message {
  text-align: center;
  color: #e74c3c;
  font-weight: 500;
  font-size: 0.875rem;
  margin-top: 1rem;
  padding: 0.75rem;
  background-color: #fdf2f2;
  border-radius: 4px;
  border: 1px solid #fecaca;
}
</style>
